import AttendeeType from 'owa-service/lib/contract/AttendeeType';
import duration from 'owa-service/lib/factory/duration';
import emailAddress from 'owa-service/lib/factory/emailAddress';
import freeBusyViewOptions from 'owa-service/lib/factory/freeBusyViewOptions';
import FreeBusyViewType from 'owa-service/lib/contract/FreeBusyViewType';
import getMailboxRequestOptions from 'owa-service-utils/lib/getMailboxRequestOptions';
import getUserAvailabilityInternalOperation from 'owa-service/lib/operation/getUserAvailabilityInternalOperation';
import GetUserAvailabilityInternalResponse from 'owa-service/lib/contract/GetUserAvailabilityInternalResponse';
import getUserAvailabilityRequest from 'owa-service/lib/factory/getUserAvailabilityRequest';
import mailboxData from 'owa-service/lib/factory/mailboxData';
import { differenceInDays, getISOString, OwaDate } from 'owa-datetime';
import { FREE_BUSY_BLOCK_SIZE_IN_MIN } from 'mini-freebusy/lib/schema/FreeBusyConstants';
import { getJsonRequestHeader } from 'owa-service/lib/ServiceRequestUtils';
import { logUsage } from 'owa-analytics';
import { MailboxInfo } from 'owa-client-ids';
import { TraceErrorObject } from 'owa-trace';

const MAX_ALLOWED_DATE_RANGE: number = 62; // Max value defined at the server side
const INVALID_TIME_INTERVAL_EXCEPTION: string = 'invalidtimeintervalexception';
const TIME_INTERVAL_EXCEEDS_LIMIT_EXCEPTION: string = 'timeintervaltoobigexception';
const MISSING_ARGUMENT_EXCEPTION: string = 'missingargumentexception';

interface UserAvailabilityService {
    /**
     * rangeStart start of request range
     */
    rangeStart: OwaDate;

    /**
     * rangeEnd end of request range
     */
    rangeEnd: OwaDate;

    /**
     * attendees array of requested mailbox
     */
    attendees: AttendeeType[];

    /**
     * requestedView FreeBusyViewType requeste
     */
    requestedView: FreeBusyViewType;

    /**
     * Requested calendar id of the event
     */
    mailboxInfo?: MailboxInfo;
}

/**
 * Makes the GetUserAvailabilityInternal call to the server to get calendar items for linked calendar
 */
export default async function getUserAvailabilityService(
    props: UserAvailabilityService
): Promise<GetUserAvailabilityInternalResponse> {
    const { rangeStart, rangeEnd, attendees, requestedView, mailboxInfo } = props;
    const header = getJsonRequestHeader();

    header.TimeZoneContext && (header.TimeZoneContext.TimeZoneDefinition.Id = 'UTC');
    let response;

    const isTimeIntervalExceedingLimit = (rangeStart: OwaDate, rangeEnd: OwaDate): boolean => {
        const DiffInDays = differenceInDays(rangeEnd, rangeStart);
        return DiffInDays > MAX_ALLOWED_DATE_RANGE;
    };

    let options = null;
    if (mailboxInfo) {
        options = getMailboxRequestOptions(mailboxInfo);
    }

    try {
        const validAttendees = attendees.filter(attendee => !!attendee.Mailbox.EmailAddress);

        if (validAttendees.length === 0) {
            throw new Error(`${MISSING_ARGUMENT_EXCEPTION} : All null elements in the array`);
        }

        if (rangeStart >= rangeEnd) {
            throw new Error(
                `${INVALID_TIME_INTERVAL_EXCEPTION} : End time is not greater than start time.`
            );
        }

        if (isTimeIntervalExceedingLimit(rangeStart, rangeEnd)) {
            logUsage('GUATimeRangeExceedsLimit', null, { isCore: true });
            throw new Error(
                `${TIME_INTERVAL_EXCEEDS_LIMIT_EXCEPTION} : Allowed date range is a max of ${MAX_ALLOWED_DATE_RANGE} days.`
            );
        }

        response = await getUserAvailabilityInternalOperation(
            {
                request: {
                    Header: header,
                    Body: getUserAvailabilityRequest({
                        MailboxDataArray: validAttendees.map(attendee =>
                            mailboxData({
                                Email: emailAddress({ Address: attendee.Mailbox.EmailAddress }),
                            })
                        ),
                        FreeBusyViewOptions: freeBusyViewOptions({
                            RequestedView: requestedView,
                            TimeWindow: duration({
                                StartTime: getISOString(rangeStart).slice(0, -1), // E.g. "2019-12-10T18:30:00.000"; getISOString(rangeStart) would be 2019-12-10T18:30:00.000Z
                                EndTime: getISOString(rangeEnd).slice(0, -1),
                            }),
                            MergedFreeBusyIntervalInMinutes: FREE_BUSY_BLOCK_SIZE_IN_MIN,
                        }),
                    }),
                },
            },
            options
        );
    } catch (err) {
        // Instrumentation to investigate MissingArgumentException error
        const traceError: TraceErrorObject = new Error(
            `[getUserAvailabilityInternal failed] with ${err}`
        );

        const errorMessage = err?.message?.toLowerCase();
        if (
            errorMessage.includes(MISSING_ARGUMENT_EXCEPTION) ||
            errorMessage.includes(TIME_INTERVAL_EXCEEDS_LIMIT_EXCEPTION) ||
            errorMessage.includes(INVALID_TIME_INTERVAL_EXCEPTION)
        ) {
            traceError.message += ' Source: OWA-Mini';
            throw traceError;
        }
        return null;
    }

    return response.Body;
}
